import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';

import { APIResult, emptyAPIResult, loadingAPIResult, successAPIResult, errorAPIResult, ApiError, defaultError } from '../../lib/redux';
import { GetPunchCardSummariesParams, UpdatePunchCardsParams, CreatePunchCardParams, triplePSDatasource } from '../../datasource';
import { PunchCard, PunchCardsForAllDrivers, PayPeriodResponse, PayPeriodSummaryResponse, DriverPayPeriodSummary } from '../../models';

import { logout } from '../Auth/authSlice';

export interface PunchCardState {
  getPunchCardSummariesAPI: APIResult<PunchCardsForAllDrivers[]>;
  refreshPunchCardSummariesAPI: APIResult<PunchCardsForAllDrivers[]>;
  updatePunchCardAPI: APIResult<PunchCard>;
  createPunchCardAPI: APIResult<PunchCard>;
  deletePunchCardAPI: APIResult<string>;
  getPayPeriodAPI: APIResult<PayPeriodResponse>;
  getPayPeriodSummariesAPI: APIResult<PayPeriodSummaryResponse>;

  punchCardSummaries: PunchCardsForAllDrivers[];
  punchCardToUpdate?: PunchCard;
  showCreatePunchCardModal?: { driverId: string };
  currentPayPeriod?: PayPeriodResponse;
  payPeriodSummaries?: DriverPayPeriodSummary[];
}

const initialState: PunchCardState = {
  getPunchCardSummariesAPI: emptyAPIResult(),
  refreshPunchCardSummariesAPI: emptyAPIResult(),
  updatePunchCardAPI: emptyAPIResult(),
  createPunchCardAPI: emptyAPIResult(),
  deletePunchCardAPI: emptyAPIResult(),
  getPayPeriodAPI: emptyAPIResult(),
  getPayPeriodSummariesAPI: emptyAPIResult(),

  punchCardSummaries: [],
  showCreatePunchCardModal: undefined,
  currentPayPeriod: undefined,
  payPeriodSummaries: undefined,
}

export const getPunchCardSummaries = createAsyncThunk<PunchCardsForAllDrivers[], GetPunchCardSummariesParams,{ rejectValue: ApiError }>(
  'punchCard/getPunchCardSummaries',
  async (params, { rejectWithValue }) => {
    try {
      const data = await triplePSDatasource.getPunchCardSummaries(params);

      return data;
    } catch (err: any) {
      const error = err.response.data?.errors?.[0] || defaultError;

      return rejectWithValue(error);
    }
  }
);

export const refreshPunchCardSummaries = createAsyncThunk<PunchCardsForAllDrivers[], GetPunchCardSummariesParams,{ rejectValue: ApiError }>(
  'punchCard/refreshPunchCardSummaries',
  async (params, { rejectWithValue }) => {
    try {
      const data = await triplePSDatasource.getPunchCardSummaries(params);

      return data;
    } catch (err: any) {
      const error = err.response.data?.errors?.[0] || defaultError;

      return rejectWithValue(error);
    }
  }
);

export const updatePunchCard = createAsyncThunk<PunchCard, UpdatePunchCardsParams,{ rejectValue: ApiError }>(
  'punchCard/updatePunchCard',
  async (params: UpdatePunchCardsParams, { rejectWithValue }) => {
    try {
      const data = await triplePSDatasource.updatePunchCard(params);

      return data;
    } catch (err: any) {
      const error = err.response.data?.errors?.[0] || defaultError;

      return rejectWithValue(error);
    }
  }
);

export const createPunchCard = createAsyncThunk<PunchCard, CreatePunchCardParams,{ rejectValue: ApiError }>(
  'punchCard/createPunchCard',
  async (params: CreatePunchCardParams, { rejectWithValue }) => {
    try {
      const data = await triplePSDatasource.createPunchCard(params);

      return data;
    } catch (err: any) {
      const error = err.response.data?.errors?.[0] || defaultError;

      return rejectWithValue(error);
    }
  }
);

export const deletePunchCard = createAsyncThunk<string, string, { rejectValue: ApiError }>(
  'punchCard/deletePunchCard',
  async (id: string, { rejectWithValue }) => {
    try {
      const data = await triplePSDatasource.deletePunchCard(id);

      return data;
    } catch (err: any) {
      const error = err.response.data?.errors?.[0] || defaultError;

      return rejectWithValue(error);
    }
  }
);

export const getCurrentPayPeriod = createAsyncThunk<PayPeriodResponse, void, { rejectValue: ApiError }>(
  'punchCard/getCurrentPayPeriod',
  async (_, { rejectWithValue }) => {
    try {
      const data = await triplePSDatasource.getCurrentPayPeriod();

      return data;
    } catch (err: any) {
      const error = err.response.data?.errors?.[0] || defaultError;

      return rejectWithValue(error);
    }
  }
);

export const getPayPeriodById = createAsyncThunk<PayPeriodResponse, string, { rejectValue: ApiError }>(
  'punchCard/getPayPeriodById',
  async (id: string, { rejectWithValue }) => {
    try {
      const data = await triplePSDatasource.getPayPeriodById(id);

      return data;
    } catch (err: any) {
      const error = err.response.data?.errors?.[0] || defaultError;

      return rejectWithValue(error);
    }
  }
);

export const getPayPeriodSummaries = createAsyncThunk<PayPeriodSummaryResponse, string, { rejectValue: ApiError }>(
  'punchCard/getPayPeriodSummaries',
  async (id: string, { rejectWithValue }) => {
    try {
      const data = await triplePSDatasource.getPayPeriodSummaries(id);

      return data;
    } catch (err: any) {
      const error = err.response.data?.errors?.[0] || defaultError;

      return rejectWithValue(error);
    }
  }
);

export const punchCardSlice = createSlice({
  name: 'punchCard',
  initialState,
  reducers: {
    setPunchCardToUpdate(state, action) { state.punchCardToUpdate = action.payload },
    resetUpdatePunchCard(state) { state.updatePunchCardAPI = initialState.updatePunchCardAPI },
    setShowCreatePunchCardModal(state, action) { state.showCreatePunchCardModal = action.payload },
    resetCreatePunchCard(state) { state.createPunchCardAPI = initialState.createPunchCardAPI },
  },
  extraReducers: builder => {
    builder
      .addCase(getPunchCardSummaries.pending, state => {
        state.getPunchCardSummariesAPI = loadingAPIResult();
      })
      .addCase(getPunchCardSummaries.fulfilled, (state, action) => {
        state.getPunchCardSummariesAPI = successAPIResult(action.payload);
        state.punchCardSummaries = action.payload;
      })
      .addCase(getPunchCardSummaries.rejected, (state, action) => {
        state.getPunchCardSummariesAPI = errorAPIResult(action.payload!);
      })
      .addCase(refreshPunchCardSummaries.pending, state => {
        state.refreshPunchCardSummariesAPI = loadingAPIResult();
      })
      .addCase(refreshPunchCardSummaries.fulfilled, (state, action) => {
        state.refreshPunchCardSummariesAPI = successAPIResult(action.payload);
        state.punchCardSummaries = action.payload;
      })
      .addCase(refreshPunchCardSummaries.rejected, (state, action) => {
        state.refreshPunchCardSummariesAPI = errorAPIResult(action.payload!);
      })
      .addCase(updatePunchCard.pending, state => {
        state.updatePunchCardAPI = loadingAPIResult();
      })
      .addCase(updatePunchCard.fulfilled, (state, action) => {
        state.updatePunchCardAPI = successAPIResult(action.payload);
      })
      .addCase(updatePunchCard.rejected, (state, action) => {
        state.updatePunchCardAPI = errorAPIResult(action.payload!);
      })
      .addCase(createPunchCard.pending, state => {
        state.createPunchCardAPI = loadingAPIResult();
      })
      .addCase(createPunchCard.fulfilled, (state, action) => {
        state.createPunchCardAPI = successAPIResult(action.payload);
        state.showCreatePunchCardModal = undefined;
      })
      .addCase(createPunchCard.rejected, (state, action) => {
        state.createPunchCardAPI = errorAPIResult(action.payload!);
      })
      .addCase(deletePunchCard.pending, state => {
        state.deletePunchCardAPI = loadingAPIResult();
      })
      .addCase(deletePunchCard.fulfilled, (state, action) => {
        state.deletePunchCardAPI = successAPIResult(action.payload);
      })
      .addCase(deletePunchCard.rejected, (state, action) => {
        state.deletePunchCardAPI = errorAPIResult(action.payload!);
      })
      .addCase(getCurrentPayPeriod.pending, state => {
        state.getPayPeriodAPI = loadingAPIResult();
      })
      .addCase(getCurrentPayPeriod.fulfilled, (state, action) => {
        state.getPayPeriodAPI = successAPIResult(action.payload);
        state.currentPayPeriod = action.payload;
      })
      .addCase(getCurrentPayPeriod.rejected, (state, action) => {
        state.getPayPeriodAPI = errorAPIResult(action.payload!);
      })
      .addCase(getPayPeriodById.pending, state => {
        state.getPayPeriodAPI = loadingAPIResult();
      })
      .addCase(getPayPeriodById.fulfilled, (state, action) => {
        state.getPayPeriodAPI = successAPIResult(action.payload);
        state.currentPayPeriod = action.payload;
      })
      .addCase(getPayPeriodById.rejected, (state, action) => {
        state.getPayPeriodAPI = errorAPIResult(action.payload!);
      })
      .addCase(getPayPeriodSummaries.pending, state => {
        state.getPayPeriodSummariesAPI = loadingAPIResult();
      })
      .addCase(getPayPeriodSummaries.fulfilled, (state, action) => {
        state.getPayPeriodSummariesAPI = successAPIResult(action.payload);
        state.payPeriodSummaries = action.payload.summaries;
      })
      .addCase(getPayPeriodSummaries.rejected, (state, action) => {
        state.getPayPeriodSummariesAPI = errorAPIResult(action.payload!);
      })
      .addMatcher(
        isAnyOf(
          logout
        ),
        state => {
          state.getPunchCardSummariesAPI = initialState.getPunchCardSummariesAPI;
          state.refreshPunchCardSummariesAPI = initialState.refreshPunchCardSummariesAPI;
          state.updatePunchCardAPI = initialState.updatePunchCardAPI;
          state.createPunchCardAPI = initialState.createPunchCardAPI;
          state.deletePunchCardAPI = initialState.deletePunchCardAPI;
          state.getPayPeriodAPI = initialState.getPayPeriodAPI;
          state.getPayPeriodSummariesAPI = initialState.getPayPeriodSummariesAPI;

          state.punchCardSummaries = initialState.punchCardSummaries;
          state.showCreatePunchCardModal = undefined;
          state.currentPayPeriod = undefined;
          state.payPeriodSummaries = undefined;
        }
      )
  }
});

export const { setPunchCardToUpdate, resetUpdatePunchCard, setShowCreatePunchCardModal, resetCreatePunchCard } = punchCardSlice.actions;
export default punchCardSlice.reducer;
